import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageSquarePlus } from 'lucide-react'
import { Button, Loading, Modal } from '@/components/ui'
import {
  assignJumpstartChatPlan,
  getJumpstartChatPlans,
  getJumpstartChatSubscriptions,
  type JumpstartChatPlan,
} from '@/api/products'
import { supabase } from '@/lib/supabase'
import { formatNIS } from '@/utils/currency'

interface OrgOption {
  id: string
  name: string
  slug: string | null
}

function getPlanPrice(plan: JumpstartChatPlan): string {
  if (plan.code === 'trial' || plan.price_monthly_agorot === 0) return 'Free'
  return `${formatNIS(plan.price_monthly_agorot)}/month`
}

function featureValue(features: Record<string, any>, key: string, fallback = '—'): string {
  const v = features?.[key]
  if (v === null) return 'Unlimited'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'number') return `${v}`
  if (typeof v === 'string' && v.length) return v
  return fallback
}

export default function JumpstartChatPage() {
  const queryClient = useQueryClient()
  const [assignOpen, setAssignOpen] = useState(false)
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [selectedPlanCode, setSelectedPlanCode] = useState('trial')
  const [billingCycleMonths, setBillingCycleMonths] = useState(1)
  const [assignStatus, setAssignStatus] = useState<'active' | 'trial'>('active')
  const [trialEndsAt, setTrialEndsAt] = useState('')

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['jumpstart-chat-plans'],
    queryFn: getJumpstartChatPlans,
    staleTime: 60_000,
  })

  const { data: subscriptions = [], isLoading: subsLoading } = useQuery({
    queryKey: ['jumpstart-chat-subscriptions'],
    queryFn: getJumpstartChatSubscriptions,
    staleTime: 30_000,
  })

  const { data: orgs = [] } = useQuery({
    queryKey: ['jumpstart-org-options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, slug')
        .order('name')
      if (error) throw error
      return (data ?? []) as OrgOption[]
    },
    staleTime: 60_000,
  })

  const orderedPlans = useMemo(() => {
    const order = ['trial', 'starter', 'pro', 'business']
    return [...plans].sort((a, b) => order.indexOf(a.code) - order.indexOf(b.code))
  }, [plans])

  const selectedPlan = useMemo(
    () => orderedPlans.find((p) => p.code === selectedPlanCode) ?? null,
    [orderedPlans, selectedPlanCode]
  )

  const defaultTrialEndDate = useMemo(() => {
    const d = new Date()
    const raw = selectedPlan?.features?.trial_days
    const days = typeof raw === 'number' && Number.isFinite(raw) ? raw : 14
    d.setDate(d.getDate() + days)
    return d.toISOString().slice(0, 10)
  }, [selectedPlan])

  const assignMutation = useMutation({
    mutationFn: async () => {
      if (!selectedOrgId) throw new Error('Please select an organization')
      if (!selectedPlanCode) throw new Error('Please select a plan')
      if (assignStatus === 'trial' && !trialEndsAt.trim()) {
        throw new Error('Please set a trial end date')
      }
      const trialIso =
        assignStatus === 'trial' ? new Date(`${trialEndsAt.trim()}T12:00:00.000Z`).toISOString() : null
      await assignJumpstartChatPlan(
        selectedOrgId,
        selectedPlanCode,
        billingCycleMonths,
        assignStatus,
        assignStatus === 'trial' ? trialIso : null
      )
    },
    onSuccess: async () => {
      setAssignOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['jumpstart-chat-subscriptions'] })
    },
  })

  const openAssign = (orgId?: string) => {
    setSelectedOrgId(orgId ?? '')
    setSelectedPlanCode('trial')
    setBillingCycleMonths(1)
    setAssignStatus('active')
    setTrialEndsAt('')
    setAssignOpen(true)
  }

  useEffect(() => {
    if (!assignOpen || assignStatus !== 'trial') return
    setTrialEndsAt(defaultTrialEndDate)
  }, [assignOpen, assignStatus, defaultTrialEndDate, selectedPlanCode])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-ink-primary">JumpStart Chat</h1>
          <p className="text-sm text-ink-secondary mt-1">
            Manage plans and subscriptions for the JumpStart Chat system
          </p>
        </div>
        <Button onClick={() => openAssign()} size="sm">
          <MessageSquarePlus className="h-4 w-4" />
          Assign to Org
        </Button>
      </div>

      <section>
        <h2 className="text-lg font-semibold text-ink-primary mb-3">Plans</h2>
        {plansLoading ? (
          <Loading text="Loading plans..." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {orderedPlans.map((plan) => (
              <div
                key={plan.id}
                className="rounded-xl p-4"
                style={{ background: '#1a1d27', border: '1px solid #2a2d3e' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-white">{plan.name}</h3>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300">
                    {plan.code}
                  </span>
                </div>
                <div className="text-amber-400 font-semibold mb-3">{getPlanPrice(plan)}</div>
                <ul className="space-y-1.5 text-xs text-ink-secondary">
                  <li>{featureValue(plan.features, 'whatsapp_devices')} devices included</li>
                  <li>Contacts: {featureValue(plan.limits, 'contacts')}</li>
                  <li>Flows: {featureValue(plan.features, 'flows_limit')}</li>
                  <li>Team agents: {featureValue(plan.features, 'team_agents')}</li>
                  <li>API access: {featureValue(plan.features, 'api_access')}</li>
                  <li>Official WhatsApp: {featureValue(plan.features, 'official_whatsapp')}</li>
                  <li>
                    Extra device: {formatNIS(Number(plan.features?.extra_device_agorot ?? 0))}/month
                  </li>
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-primary mb-3">Subscriptions</h2>
        {subsLoading ? (
          <Loading text="Loading subscriptions..." />
        ) : (
          <div className="rounded-xl overflow-hidden responsive-table-wrapper" style={{ border: '1px solid #2a2d3e' }}>
            <table className="w-full text-sm min-w-[640px]" style={{ background: '#1a1d27' }}>
              <thead>
                <tr style={{ background: '#252836', borderBottom: '1px solid #2a2d3e' }}>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-ink-muted">Org</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-ink-muted">Plan</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-ink-muted">Status</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-ink-muted">Billing</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wide text-ink-muted">Period End</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wide text-ink-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-ink-secondary">
                      No subscriptions yet.
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((sub) => (
                    <tr key={sub.id} style={{ borderTop: '1px solid #2a2d3e' }}>
                      <td className="px-4 py-3 text-ink-primary">
                        {sub.org_name ?? sub.org_id}
                        {sub.slug ? <span className="text-xs text-ink-muted ml-2">({sub.slug})</span> : null}
                      </td>
                      <td className="px-4 py-3 text-indigo-300 font-mono">{sub.plan_code}</td>
                      <td className="px-4 py-3 text-ink-secondary capitalize">{sub.status}</td>
                      <td className="px-4 py-3 text-ink-secondary">{sub.billing_cycle_months} month(s)</td>
                      <td className="px-4 py-3 text-ink-secondary">
                        {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openAssign(sub.org_id)}
                          className="text-xs px-3 py-1 rounded-lg bg-indigo-500/20 text-indigo-300 hover:text-indigo-200"
                        >
                          Assign Plan
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        title="Assign JumpStart Chat Plan"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={() => assignMutation.mutate()} loading={assignMutation.isPending}>
              Save
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          {assignMutation.error ? (
            <p className="text-sm text-red-400">{(assignMutation.error as Error).message}</p>
          ) : null}

          <div>
            <label className="block text-xs text-ink-muted mb-1">Organization</label>
            <select
              className="w-full rounded-lg bg-navy-card border border-navy-border px-3 py-2 text-sm text-ink-primary"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
            >
              <option value="">Select organization</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} {org.slug ? `(${org.slug})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-ink-muted mb-1">Plan</label>
            <select
              className="w-full rounded-lg bg-navy-card border border-navy-border px-3 py-2 text-sm text-ink-primary"
              value={selectedPlanCode}
              onChange={(e) => setSelectedPlanCode(e.target.value)}
            >
              {orderedPlans.map((plan) => (
                <option key={plan.id} value={plan.code}>
                  {plan.name} ({plan.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-ink-muted mb-1">Billing cycle</label>
            <select
              className="w-full rounded-lg bg-navy-card border border-navy-border px-3 py-2 text-sm text-ink-primary"
              value={billingCycleMonths}
              onChange={(e) => setBillingCycleMonths(Number(e.target.value))}
            >
              <option value={1}>1 month</option>
              <option value={3}>3 months</option>
              <option value={12}>12 months</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-ink-muted mb-1">Status</label>
            <select
              className="w-full rounded-lg bg-navy-card border border-navy-border px-3 py-2 text-sm text-ink-primary"
              value={assignStatus}
              onChange={(e) => setAssignStatus(e.target.value as 'active' | 'trial')}
            >
              <option value="active">Active</option>
              <option value="trial">Trial</option>
            </select>
          </div>

          {assignStatus === 'trial' && (
            <div>
              <label className="block text-xs text-ink-muted mb-1">Trial end</label>
              <input
                type="date"
                className="w-full rounded-lg bg-navy-card border border-navy-border px-3 py-2 text-sm text-ink-primary"
                value={trialEndsAt}
                onChange={(e) => setTrialEndsAt(e.target.value)}
              />
              <p className="text-[11px] text-ink-muted mt-1">
                Default uses plan <code className="text-indigo-300">trial_days</code> or 14 days from today.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}
